import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CaretLeft, CaretRight, ShieldWarning, CheckCircle } from '@phosphor-icons/react';
import { getConflictTypeLabel } from '../utils/conflictDetection';
import { cn } from '../lib/utils';

const MixedMessagesModal = ({ 
  isOpen, 
  onClose, 
  conflicts = [], 
  isLoadingConflicts = false, 
  isProcessingConflicts = false,
  className = "",
  hideHeader = false,
  showOnlyAffectedSettings = false,
  onToggleAffectedFilter,
  onItemSelect,
  selectedItem,
  currentConflictIndex: externalConflictIndex = 0,
  onCurrentConflictIndexChange
}) => {
  const [currentConflictIndex, setCurrentConflictIndex] = useState(externalConflictIndex);

  // Sync with external conflict index
  useEffect(() => {
    setCurrentConflictIndex(externalConflictIndex);
  }, [externalConflictIndex]);

  // Navigate to next conflict
  const nextConflict = () => {
    const newIndex = currentConflictIndex < conflicts.length - 1 ? currentConflictIndex + 1 : 0;
    setCurrentConflictIndex(newIndex);
    if (onCurrentConflictIndexChange) {
      onCurrentConflictIndexChange(newIndex);
    }
  };

  // Navigate to previous conflict
  const prevConflict = () => {
    const newIndex = currentConflictIndex > 0 ? currentConflictIndex - 1 : conflicts.length - 1;
    setCurrentConflictIndex(newIndex);
    if (onCurrentConflictIndexChange) {
      onCurrentConflictIndexChange(newIndex);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={cn(
      "flex flex-col h-full bg-gray-50 border-l border-gray-200/50",
      className
    )}>
      {/* Elegant Header - Only show if not hidden */}
      {!hideHeader && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="flex items-center justify-between p-6 border-b border-gray-200/50 bg-white/70 backdrop-blur-sm"
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center shadow-inner">
              <ShieldWarning className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <h2 className="font-light text-gray-900 text-lg tracking-tight">Mixed Messages</h2>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-white/60 transition-colors"
            title="Close Mixed Messages"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </motion.button>
        </motion.div>
      )}

      {/* Content Area */}
      <div className={cn("flex-1 overflow-y-auto", hideHeader ? "p-4" : "p-4")}>
        {/* Loading State */}
        {isProcessingConflicts ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="h-full flex flex-col justify-center items-center text-center max-w-md mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center shadow-inner mb-6"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-8 h-8 border-2 border-gray-300 border-t-red-500 rounded-full"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <h3 className="font-light text-gray-900 text-xl mb-3 tracking-tight">Analyzing Settings</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Checking for conflicts in your brain settings...
              </p>
            </motion.div>
          </motion.div>
        ) : conflicts.length === 0 ? (
          /* No Conflicts State */
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="h-full flex flex-col justify-center items-center text-center max-w-md mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center shadow-inner mb-6"
            >
              <CheckCircle className="w-8 h-8 text-green-600" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="mb-8"
            >
              <h3 className="font-light text-gray-900 text-xl mb-3 tracking-tight">All Clear!</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Your brain settings are consistent and conflict-free. Customers will receive clear, unified responses.
              </p>
            </motion.div>
          </motion.div>
        ) : (
          /* Conflicts Display */
          <div className="space-y-4">
            {/* Header with conflict count and navigation */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="flex items-center justify-between"
            >
              <div>
                <h3 className="font-medium text-gray-900 text-lg">
                  {conflicts.length} Conflict{conflicts.length !== 1 ? 's' : ''} Found
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Fix these so customers get the same answers
                </p>
              </div>
              {conflicts.length > 1 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500 font-medium">
                    {currentConflictIndex + 1} of {conflicts.length}
                  </span>
                </div>
              )}
            </motion.div>

            {/* Current Conflict Card */}
            <AnimatePresence mode="wait">
              {conflicts.length > 0 && (
                <motion.div
                  key={currentConflictIndex}
                  initial={{ opacity: 0, x: 20, scale: 0.98 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -20, scale: 0.98 }}
                  transition={{ 
                    duration: 0.4,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                  className="rounded-xl border border-gray-200 bg-white overflow-hidden"
                >
                  {(() => {
                    const conflict = conflicts[currentConflictIndex];
                    if (!conflict) return null;

                    return (
                      <div className="p-3">
                        {/* Problem Description - What's Wrong */}
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1, duration: 0.3 }}
                          className="border-l-4 border-red-200 bg-red-50 rounded-r-lg mb-4"
                        >
                          <div className="px-4 py-1">
                            <p className="text-sm font-medium text-red-900 mb-1">Problem</p>
                            <p className="text-sm text-red-800 leading-relaxed">
                              {conflict.reason}
                            </p>
                          </div>
                        </motion.div>

                        {/* Affected Settings - What's Impacted */}
                        {conflict.settings && conflict.settings.length > 0 && (
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2, duration: 0.3 }}
                            className="space-y-2 mb-4"
                          >
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-600">
                                Affects {conflict.settings.length} setting{conflict.settings.length !== 1 ? 's' : ''}
                              </p>
                              {onToggleAffectedFilter && (
                                <button
                                  onClick={onToggleAffectedFilter}
                                  className={cn(
                                    "text-xs font-medium transition-colors duration-200 underline decoration-1 underline-offset-2",
                                    showOnlyAffectedSettings 
                                      ? "text-red-600 hover:text-red-700"
                                      : "text-gray-500 hover:text-gray-700"
                                  )}
                                >
                                  {showOnlyAffectedSettings ? 'Show all' : 'Filter to these'}
                                </button>
                              )}
                            </div>
                                                        <div className="flex flex-wrap gap-1">
                                {conflict.settings.map((setting, settingIndex) => {
                                  const isSelected = selectedItem?.id === setting.id && selectedItem?.type === 'setting';
                                  return (
                                                                        <motion.button
                                    key={setting.id || settingIndex}
                                      onClick={() => {
                                        if (onItemSelect) {
                                          onItemSelect({
                                            ...setting,
                                            type: 'setting',
                                            category_name: setting.category_name || 'Unknown Category'
                                          });
                                        }
                                      }}
                                      className={cn(
                                        "inline-flex items-center px-2 py-1 rounded text-xs transition-colors cursor-pointer",
                                        isSelected 
                                          ? "bg-gray-700 text-white font-medium shadow-sm" 
                                          : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-700"
                                      )}
                                      title={`Open ${setting.name}`}
                                  >
                                    {setting.name}
                                    </motion.button>
                                  );
                                })}
                            </div>
                          </motion.div>
                          )}

                        {/* Separator */}
                        <div className="border-t border-gray-200 mb-4"></div>

                        {/* Solution - How to Fix */}
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3, duration: 0.3 }}
                          className="border-l-4 border-green-200 bg-green-50 rounded-r-lg"
                        >
                          <div className="px-4 py-1">
                            <p className="text-sm font-medium text-green-900 mb-1">Suggested Solution</p>
                            <p className="text-sm text-green-800 leading-relaxed">
                                {conflict.suggestion}
                              </p>
                          </div>
                        </motion.div>
                      </div>
                    );
                  })()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Navigation Footer */}
      {conflicts.length > 1 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="border-t border-gray-200/50 p-6 bg-white/70 backdrop-blur-sm"
        >
          <div className="flex items-center justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={prevConflict}
              className="p-3 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 text-gray-600 hover:from-gray-100 hover:to-gray-200 shadow-inner transition-all border border-gray-200/50"
              title="Previous conflict"
            >
              <CaretLeft className="w-4 h-4" />
            </motion.button>

            {/* Pagination dots */}
            <div className="flex items-center space-x-2">
              {conflicts.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => {
                    setCurrentConflictIndex(index);
                    if (onCurrentConflictIndexChange) {
                      onCurrentConflictIndexChange(index);
                    }
                  }}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  className={cn(
                    "h-2 rounded-full transition-all duration-300",
                    index === currentConflictIndex 
                      ? 'bg-red-500 w-8 shadow-sm' 
                      : 'bg-gray-300 w-2 hover:bg-gray-400'
                  )}
                  title={`Go to conflict ${index + 1}`}
                />
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={nextConflict}
              className="p-3 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 text-gray-600 hover:from-gray-100 hover:to-gray-200 shadow-inner transition-all border border-gray-200/50"
              title="Next conflict"
            >
              <CaretRight className="w-4 h-4" />
            </motion.button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default MixedMessagesModal; 